import { z } from 'zod/v4';
import { notificationChannelEnum as notificationChannelSchema, type NotificationChannel } from '../enums/index';
import { segmentDefinitionSchema } from './audience-segment';

/**
 * Рекурсивный тип значения условия триггера.
 * Структурно идентичен TriggerConditionValue из @masters-club/types
 * (validation не зависит от types — дублируем структуру локально, чтобы
 * inferred-тип схемы был присваиваемым к TriggerConditions потребителями).
 */
type TriggerConditionValue =
  | string
  | number
  | boolean
  | null
  | TriggerConditionValue[]
  | { [key: string]: TriggerConditionValue };

/**
 * Рекурсивная схема значения условия триггера.
 * Соответствует TriggerConditionValue из @masters-club/types:
 * примитив | массив | вложенный объект.
 */
const triggerConditionValueSchema: z.ZodType<TriggerConditionValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(triggerConditionValueSchema),
    z.record(z.string(), triggerConditionValueSchema),
  ]),
);

/**
 * Условия триггера — плоский (или вложенный) объект ключ→значение.
 * Пустой объект {} означает «срабатывает всегда».
 */
export const triggerConditionsSchema = z.record(z.string(), triggerConditionValueSchema);

/**
 * Схема создания триггера
 */
export const createTriggerSchema = z.object({
  name: z.string({ message: 'Название обязательно' })
    .min(1, 'Название обязательно')
    .max(200, 'Название слишком длинное (максимум 200 символов)'),
  eventType: z.string({ message: 'Тип события обязателен' })
    .min(1, 'Тип события обязателен')
    .max(100, 'Тип события слишком длинный (максимум 100 символов)'),
  conditions: triggerConditionsSchema.optional(),
  actions: z.array(z.record(z.string(), z.any())).min(1, 'Требуется хотя бы одно действие'), // JSON structure
  delay: z.number().int('Задержка должна быть целым числом').min(0, 'Задержка не может быть отрицательной').optional(),
  isActive: z.boolean({ message: 'Флаг активности обязателен' }),
  /**
   * Аудитория, на которую распространяется триггер. Не указано → ограничений нет
   * (per-user события идут единственному userId из payload; broadcast-события — никому).
   * inline definition имеет приоритет над audienceSegmentId.
   */
  audienceDefinition: segmentDefinitionSchema.nullable().optional(),
  audienceSegmentId: z.number().int().positive().nullable().optional(),
});

/**
 * Схема обновления триггера
 */
export const updateTriggerSchema = createTriggerSchema.partial();

/**
 * Схема создания рассылки
 */
export const createBroadcastSchema = z.object({
  name: z.string({ message: 'Название обязательно' })
    .min(1, 'Название обязательно')
    .max(200, 'Название слишком длинное (максимум 200 символов)'),
  messageTemplate: z.string({ message: 'Шаблон сообщения обязателен' })
    .min(1, 'Шаблон сообщения обязателен')
    .max(10000, 'Шаблон сообщения слишком длинный'),
  parseMode: z.enum(['HTML', 'Markdown']).optional(),
  channels: z.array(notificationChannelSchema).min(1, 'Требуется хотя бы один канал'),
  payload: z.object({
    telegram: z.object({
      media: z.object({
        type: z.enum(['photo', 'video', 'document']),
        url: z.string().url(),
        caption: z.string().optional(),
        parseMode: z.enum(['HTML', 'Markdown']).optional(),
      }).optional(),
      buttons: z.array(z.object({
        text: z.string().min(1),
        url: z.string().url().optional(),
        callbackData: z.string().optional(),
      })).optional(),
    }).optional(),
  }).optional(),
  filters: z.object({
    isActive: z.boolean().optional(),
    languages: z.array(z.string()).optional(),
    hasTelegram: z.boolean().optional(),
    hasEmail: z.boolean().optional(),
    hasActiveSubscription: z.boolean().optional(),
    registeredAfter: z.string().optional(),
    registeredBefore: z.string().optional(),
    productIds: z.array(z.string()).optional(),
    courseIds: z.array(z.string()).optional(),
    channelIds: z.array(z.string()).optional(),
    userIds: z.array(z.number()).optional(),
  }).optional(),
  scheduledAt: z.string().datetime('Некорректный формат даты').optional(),
  translations: z.array(z.object({
    languageCode: z.string().min(1),
    messageTemplate: z.string().min(1),
  })).optional(),
});

/**
 * Схема отправки уведомления
 */
export const sendNotificationSchema = z.object({
  userId: z.string({ message: 'ID пользователя обязателен' })
    .min(1, 'ID пользователя обязателен'),
  channel: notificationChannelSchema.optional(),
  title: z.string({ message: 'Заголовок обязателен' })
    .min(1, 'Заголовок обязателен')
    .max(200, 'Заголовок слишком длинный (максимум 200 символов)'),
  message: z.string({ message: 'Сообщение обязательно' })
    .min(1, 'Сообщение обязательно')
    .max(4000, 'Сообщение слишком длинное (максимум 4000 символов)'),
  linkUrl: z.string().url('Некорректный URL').optional(),
});

// Type exports
export type { NotificationChannel };
export type CreateTriggerInput = z.infer<typeof createTriggerSchema>;
export type UpdateTriggerInput = z.infer<typeof updateTriggerSchema>;
export type CreateBroadcastInput = z.infer<typeof createBroadcastSchema>;
export type SendNotificationInput = z.infer<typeof sendNotificationSchema>;

// ==================== Response Types ====================
// Note: BroadcastWithTranslations экспортируется из backend/domains/notifications/broadcasts.service.ts
// PreviewResult также экспортируется из broadcasts.service.ts
